import * as React from "react";
import { LanguageGroup, LltsLanguage, UserAccountsService, UtilsService } from "gen/clients/llts";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { Form, Formik } from "formik";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import DialogActions from "@mui/material/DialogActions";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { useMutation, useQuery } from "react-query";
import { DialogBody } from "components/DialogBody/DialogBody";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { useTranslation } from "react-i18next";
import { SelectOption } from "@mui/base";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";

interface Props {
  languageGroupIndex: number;
  languageGroups: LanguageGroup[];
  onClose: () => void;
  onSuccess: (languageGroup: LanguageGroup) => void;
}

enum FieldName {
  name = "name",
  languages = "languages"
}

interface FormValues {
  [FieldName.name]: string;
  [FieldName.languages]: SelectOption<string>[];
}

const EditLanguageGroupDialog: React.FC<Props> = ({ languageGroupIndex, languageGroups, onClose, onSuccess }) => {
  const { t } = useTranslation();

  const languageGroup = React.useMemo(() => languageGroups[languageGroupIndex], [languageGroupIndex, languageGroups]);

  const {
    data: languages,
    isLoading: areLanguagesLoading,
    error: languagesError
  } = useQuery(["listLltsLanguages"], {
    queryFn: () => UtilsService.listLltsLanguages({})
  });

  const { mutateAsync, error: saveError } = useMutation(UserAccountsService.storeLanguageGroups);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const selectedLltsLanguages: LltsLanguage[] = values[FieldName.languages]
        .map(l => languages?.find(lang => lang.id === l.value))
        .filter(l => l !== undefined) as LltsLanguage[];
      const editedLanguageGroup: LanguageGroup = {
        name: values[FieldName.name],
        scope: languageGroup.scope,
        languages: selectedLltsLanguages
      };
      const newLanguageGroups = [...languageGroups];
      newLanguageGroups[languageGroupIndex] = editedLanguageGroup;
      await mutateAsync(
        {
          groupScope: languageGroup.scope,
          requestBody: newLanguageGroups
        },
        { onSuccess: () => onSuccess(editedLanguageGroup) }
      );
    },
    [languageGroup.scope, languageGroupIndex, languageGroups, languages, mutateAsync, onSuccess]
  );

  const languageOptions = React.useMemo(
    () => languages?.map(l => ({ label: l.name, value: `${l.id}` })).sort(SELECT_OPTION_COMPARATOR) || [],
    [languages]
  );

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle>{t("accountSettings.languageGroups.editDialog.title")}</DialogTitle>
      <DialogBody isLoading={areLanguagesLoading} error={languagesError}>
        <Formik
          initialValues={{
            name: languageGroup.name,
            languages: languageGroup.languages?.map(l => ({ label: l.name, value: `${l.id}` })) || []
          }}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form noValidate={true} autoComplete="off" autoCorrect="off">
              <DialogContent>
                <Stack spacing={2}>
                  <TextInputField
                    name={FieldName.name}
                    label={t("accountSettings.languageGroups.editDialog.languageGroupName")}
                    required={true}
                    requiredMessage={t("accountSettings.languageGroups.editDialog.languageGroupNameRequired")}
                    variant="outlined"
                    autoComplete="off"
                  />
                  <SelectManyAutocompleteField
                    name={FieldName.languages}
                    label={t("accountSettings.languageGroups.editDialog.languages")}
                    options={languageOptions}
                    required={true}
                    requiredMessage={t("accountSettings.languageGroups.editDialog.languagesRequired")}
                    variant="outlined"
                  />
                  {saveError && <ApiErrorMessage apiError={saveError} />}
                </Stack>
              </DialogContent>

              <DialogActions>
                <LoadingButton isLoading={isSubmitting}>
                  {t("accountSettings.languageGroups.editDialog.apply")}
                </LoadingButton>
                <Button onClick={onClose}>{t("common.cancel")}</Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogBody>
    </Dialog>
  );
};

export { EditLanguageGroupDialog };
